import React, { useState, useEffect, useRef } from 'react';
import styles from './styles.css';
import { default as LoadingSpinner } from '../LoadingSpinner/index.jsx';
import { useTranslation } from 'react-i18next';

/**
 * I decided not to fix this / maintain twice and added the static footer to the data page instead
 * @param slides
 * @returns {JSX.Element}
 * @constructor
 */

const SavedCarousel = ({ slides }) => {
	const { t } = useTranslation();
	const carouselRef = useRef(null);
	const innerRef = useRef(null);
	const resizeTimeoutRef = useRef(null);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [visibleItems, setVisibleItems] = useState(1);

	// Calculate visible items based on screen size
	const calculateVisibleItems = () => {
		const width = window.innerWidth;
		if (width >= 992) return 3;
		if (width >= 768) return 2;
		return 1;
	};

	// Update dimensions on load and resize - wait for DOM to be ready
	useEffect(() => {
		// Don't calculate if no slides (data not loaded yet)
		if (slides.length === 0) {
			return;
		}

		const updateDimensions = () => {
			// Clear any pending resize updates
			if (resizeTimeoutRef.current) {
				clearTimeout(resizeTimeoutRef.current);
			}

			// Debounce resize updates to avoid excessive calculations
			resizeTimeoutRef.current = setTimeout(() => {
				const visible = calculateVisibleItems();
				setVisibleItems((prevVisible) => {
					// If visible items changed, adjust currentIndex to stay within bounds
					if (prevVisible !== visible) {
						// Adjust currentIndex to stay within valid range for new visible count
						setCurrentIndex((prevIndex) => {
							const maxIndex = Math.max(0, slides.length - visible);
							return Math.min(prevIndex, maxIndex);
						});
					}
					return visible;
				});
			}, 150); // 150ms debounce for resize
		};

		// Initial calculation - wait for DOM to be ready (double RAF ensures layout is complete)
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				const visible = calculateVisibleItems();
				setVisibleItems(visible);
			});
		});

		// Add resize listener that runs on every resize
		window.addEventListener('resize', updateDimensions);
		return () => {
			window.removeEventListener('resize', updateDimensions);
			if (resizeTimeoutRef.current) {
				clearTimeout(resizeTimeoutRef.current);
			}
		};
	}, [slides.length]);

	const handleNext = () => {
		const totalItems = slides.length;
		if (totalItems === 0) return;
		const maxIndex = Math.max(0, totalItems - visibleItems);
		const nextIndex = currentIndex >= maxIndex ? 0 : currentIndex + 1;
		setCurrentIndex(nextIndex);
	};

	const handlePrev = () => {
		const totalItems = slides.length;
		if (totalItems === 0) return;
		const maxIndex = Math.max(0, totalItems - visibleItems);
		const prevIndex = currentIndex <= 0 ? maxIndex : currentIndex - 1;
		setCurrentIndex(prevIndex);
	};

	// Calculate and apply transform based on actual item width
	useEffect(() => {
		if (!innerRef.current || slides.length === 0) return;

		const updateTransform = () => {
			if (!innerRef.current) return;

			// Double RAF to ensure layout is complete (especially when visibleItems changes)
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					if (!innerRef.current) return;

					// Get the first item to measure its width
					const firstItem = innerRef.current.querySelector('.saved-carousel-item');
					if (!firstItem) return;

					// Get computed styles to account for gap
					const computedStyle = window.getComputedStyle(innerRef.current);
					const gap = parseFloat(computedStyle.gap) || 0;

					// Get item width
					const itemRect = firstItem.getBoundingClientRect();
					const itemWidth = itemRect.width;

					// Calculate transform: move by (item width + gap) * currentIndex
					const translateX = currentIndex * (itemWidth + gap);
					innerRef.current.style.transform = `translateX(-${translateX}px)`;
				});
			});
		};

		// Update transform when currentIndex, visibleItems, or slides change
		updateTransform();

		// Also update on resize (debounced)
		let resizeTimeout;
		const handleResize = () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(updateTransform, 100);
		};
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
			clearTimeout(resizeTimeout);
		};
	}, [currentIndex, visibleItems, slides.length]);

	// Ensure currentIndex stays within bounds when visibleItems or slides change
	useEffect(() => {
		if (slides.length === 0) return;
		const maxIndex = Math.max(0, slides.length - visibleItems);
		setCurrentIndex((prevIndex) => Math.min(prevIndex, maxIndex));
	}, [slides.length, visibleItems]);

	// Auto-scroll logic
	useEffect(() => {
		if (slides.length === 0 || slides.length <= visibleItems) return; // Don't auto-scroll if no slides or all items are visible

		const autoScroll = setInterval(() => {
			setCurrentIndex((prevIndex) => {
				const totalItems = slides.length;
				if (totalItems === 0) return prevIndex;
				const maxIndex = Math.max(0, totalItems - visibleItems);
				return prevIndex >= maxIndex ? 0 : prevIndex + 1;
			});
		}, 5000);

		return () => clearInterval(autoScroll); // Cleanup on unmount
	}, [slides.length, visibleItems]);

	// Touch gesture support
	let startX = 0;
	let isDragging = false;

	const handleTouchStart = (e) => {
		startX = e.touches[0].clientX;
		isDragging = true;
	};

	const handleTouchMove = (e) => {
		if (!isDragging) return;
		const moveX = e.touches[0].clientX - startX;
		if (Math.abs(moveX) > 50) {
			if (moveX > 0) {
				handlePrev();
			} else {
				handleNext();
			}
			isDragging = false;
		}
	};

	const handleTouchEnd = () => {
		isDragging = false;
	};

	return (
		<div className="saved-slider-wrapper" dir="ltr">
			<div className="uk-container">
				<div
					className="saved-slider uk-flex uk-flex-wrap uk-margin-remove uk-padding-small"
					id="saved-slider"
					ref={carouselRef}
					onTouchStart={handleTouchStart}
					onTouchMove={handleTouchMove}
					onTouchEnd={handleTouchEnd}
					style={{ overflow: 'hidden' }}
				>
					<h2>{t('MIF views')}</h2>
					{/*<button*/}
					{/*	className={`saved-carousel-control-prev ${styles.previousArrow}`}*/}
					{/*	onClick={handlePrev}*/}
					{/*>*/}
					{/*	{t("Previous")}*/}
					{/*</button>*/}
					<div
						ref={innerRef}
						className="saved-carousel-inner"
						style={{
							display: 'flex',
							transition: 'transform 0.3s ease',
							width: '100%',
						}}
					>
						{slides.map((slide, index) => {
							// Calculate flex-basis accounting for gap (similar to CSS calc)
							let flexBasis;
							if (visibleItems === 1) {
								flexBasis = '100%';
							} else if (visibleItems === 2) {
								flexBasis = 'calc(50% - 10px)'; // 50% minus half the 20px gap
							} else {
								// 3 items: 33.33% minus gap adjustment
								flexBasis = 'calc(33.333% - 13.33333px)';
							}

							return (
								<div className="saved-carousel-item" key={index} style={{ flex: `0 0 ${flexBasis}` }}>
									<h3>
										<img className="view-icon" src={slide.viewIcon} alt={slide.viewName} />
										<span className="view-name">{slide.viewName}</span>
									</h3>
									<h2>{slide.title}</h2>
									<h6>{slide.description}</h6>
									<div className="saved-carousel-subject-icons">
										{slide.measIcons.map((icon, i) => (
											<div className="saved-carousel-subject-icon" key={i}>
												<img src={icon.src} alt={icon.alt} />
											</div>
										))}
									</div>
									<a className={`btn btn-outline-primary ${styles.viewButton}`} href={slide.link}>
										{t('View now')}
									</a>
								</div>
							);
						})}
					</div>
					{/*<button*/}
					{/*	className={`saved-carousel-control-next ${styles.nextArrow}`}*/}
					{/*	onClick={handleNext}*/}
					{/*>*/}
					{/*	{t("Next")}*/}
					{/*</button>*/}
				</div>
			</div>
		</div>
	);
};

export default SavedCarousel;
